import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { api } from "../App";
import { useTranslation } from 'react-i18next';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { TrendingUp, PieChart as PieChartIcon, BarChart3 } from "lucide-react";
import { LoadingOverlay } from "./LoadingOverlay";

const COLORS = {
  submitted: "#3B82F6",
  approved: "#10B981",
  rejected: "#EF4444",
  document_verification: "#F59E0B",
  field_verification: "#8B5CF6",
  under_review: "#6366F1",
};

// Custom hook for window size
const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth });
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
};

export default function AnalyticsDashboard() {
  const { t } = useTranslation();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const { width } = useWindowSize();
  const isMobile = width < 640;

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const response = await api.get("/dashboard/analytics");
      setAnalytics(response.data);
    } catch (error) {
      console.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingOverlay fullPage={false} />;
  }

  if (!analytics) return null;

  // Prepare data for charts
  const statusData = Object.entries(analytics.status_distribution).map(([status, count]) => ({
    name: isMobile ? t(`status.${status}`).substring(0, 8) : t(`status.${status}`),
    fullName: t(`status.${status}`),
    value: count,
    color: COLORS[status] || "#94A3B8",
  }));

  const approvalData = [
    { name: isMobile ? t('stats.approved').substring(0, 6) : t('stats.approved'), fullName: t('stats.approved'), value: analytics.approval_metrics.approved, color: COLORS.approved },
    { name: isMobile ? t('stats.rejected').substring(0, 6) : t('stats.rejected'), fullName: t('stats.rejected'), value: analytics.approval_metrics.rejected, color: COLORS.rejected },
    { name: isMobile ? t('stats.pending').substring(0, 6) : t('stats.pending'), fullName: t('stats.pending'), value: analytics.approval_metrics.pending, color: "#94A3B8" },
  ];

  // Chart dimensions based on screen size
  const chartHeight = isMobile ? 220 : 300;
  const pieOuterRadius = isMobile ? 60 : 100;
  const fontSize = isMobile ? "10px" : "12px";

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="analytics-dashboard">
      <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
        <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-700" />
        <h2 className="text-xl sm:text-2xl font-manrope font-bold text-slate-900 tracking-tight">
          {t('analytics.title')}
        </h2>
      </div>

      {/* Approval Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <Card className="p-3 sm:p-5 bg-gradient-to-br from-emerald-50 to-white border border-emerald-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-slate-600 mb-1">{t('analytics.approvalRate')}</p>
              <p className="text-2xl sm:text-3xl font-manrope font-bold text-emerald-700">
                {analytics.approval_metrics.approval_rate}%
              </p>
            </div>
            <TrendingUp className="w-8 h-8 sm:w-10 sm:h-10 text-emerald-600 opacity-50" />
          </div>
        </Card>
        <Card className="p-3 sm:p-5 bg-gradient-to-br from-red-50 to-white border border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-slate-600 mb-1">{t('analytics.rejectionRate')}</p>
              <p className="text-2xl sm:text-3xl font-manrope font-bold text-red-700">
                {analytics.approval_metrics.rejection_rate}%
              </p>
            </div>
            <TrendingUp className="w-8 h-8 sm:w-10 sm:h-10 text-red-600 opacity-50 rotate-180" />
          </div>
        </Card>
        <Card className="p-3 sm:p-5 bg-gradient-to-br from-blue-50 to-white border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-slate-600 mb-1">{t('analytics.processingTime')}</p>
              <p className="text-2xl sm:text-3xl font-manrope font-bold text-blue-700">~3d</p>
              <p className="text-[10px] sm:text-xs text-slate-500">{t('analytics.average')}</p>
            </div>
            <PieChartIcon className="w-8 h-8 sm:w-10 sm:h-10 text-blue-600 opacity-50" />
          </div>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Monthly Trends */}
        <Card className="p-4 sm:p-6 bg-white border border-stone-200 shadow-sm">
          <h3 className="text-base sm:text-lg font-manrope font-bold text-slate-900 mb-3 sm:mb-4">
            {t('analytics.monthlyTrends')}
          </h3>
          <ResponsiveContainer width="100%" height={chartHeight}>
            <LineChart data={analytics.monthly_trends} margin={isMobile ? { left: -20, right: 5 } : { left: 0, right: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis 
                dataKey="month" 
                stroke="#64748B" 
                style={{ fontSize }} 
                tick={{ fontSize: isMobile ? 9 : 12 }}
                interval={isMobile ? 1 : 0}
              />
              <YAxis 
                stroke="#64748B" 
                style={{ fontSize }} 
                tick={{ fontSize: isMobile ? 9 : 12 }}
                width={isMobile ? 30 : 40}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid #E5E7EB",
                  borderRadius: "8px",
                  fontSize: isMobile ? "11px" : "14px",
                }}
              />
              {!isMobile && <Legend />}
              <Line
                type="monotone"
                dataKey="submitted"
                stroke={COLORS.submitted}
                strokeWidth={isMobile ? 1.5 : 2}
                name={t('status.submitted')}
                dot={!isMobile}
              />
              <Line
                type="monotone"
                dataKey="approved"
                stroke={COLORS.approved}
                strokeWidth={isMobile ? 1.5 : 2}
                name={t('status.approved')}
                dot={!isMobile}
              />
              <Line
                type="monotone"
                dataKey="rejected"
                stroke={COLORS.rejected}
                strokeWidth={isMobile ? 1.5 : 2}
                name={t('status.rejected')}
                dot={!isMobile}
              />
            </LineChart>
          </ResponsiveContainer>
          {/* Mobile Legend */}
          {isMobile && (
            <div className="flex justify-center gap-4 mt-2 text-[10px]">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS.submitted }}></span>
                {t('status.submitted')}
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS.approved }}></span>
                {t('status.approved')}
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS.rejected }}></span>
                {t('status.rejected')}
              </span>
            </div>
          )}
        </Card>

        {/* Approval Distribution */}
        <Card className="p-4 sm:p-6 bg-white border border-stone-200 shadow-sm">
          <h3 className="text-base sm:text-lg font-manrope font-bold text-slate-900 mb-3 sm:mb-4">
            {t('analytics.approvalDistribution')}
          </h3>
          <ResponsiveContainer width="100%" height={chartHeight}>
            <PieChart>
              <Pie
                data={approvalData}
                cx="50%"
                cy="50%"
                labelLine={!isMobile}
                label={isMobile ? false : ({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={pieOuterRadius}
                fill="#8884d8"
                dataKey="value"
              >
                {approvalData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value, name, props) => [value, props.payload.fullName]}
                contentStyle={{
                  fontSize: isMobile ? "11px" : "14px",
                }}
              />
              {!isMobile && <Legend />}
            </PieChart>
          </ResponsiveContainer>
          {/* Mobile Legend for Pie Chart */}
          {isMobile && (
            <div className="flex justify-center gap-3 mt-2 text-[10px]">
              {approvalData.map((item, index) => (
                <span key={index} className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }}></span>
                  {item.fullName}: {item.value}
                </span>
              ))}
            </div>
          )}
        </Card>

        {/* Status Distribution */}
        <Card className="p-4 sm:p-6 bg-white border border-stone-200 shadow-sm lg:col-span-2">
          <h3 className="text-base sm:text-lg font-manrope font-bold text-slate-900 mb-3 sm:mb-4">
            {t('analytics.statusDistribution')}
          </h3>
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart 
              data={statusData} 
              margin={isMobile ? { left: -20, right: 5, bottom: 60 } : { left: 0, right: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis 
                dataKey="name" 
                stroke="#64748B" 
                style={{ fontSize }} 
                tick={{ fontSize: isMobile ? 8 : 12 }}
                angle={isMobile ? -45 : 0}
                textAnchor={isMobile ? "end" : "middle"}
                height={isMobile ? 60 : 30}
                interval={0}
              />
              <YAxis 
                stroke="#64748B" 
                style={{ fontSize }} 
                tick={{ fontSize: isMobile ? 9 : 12 }}
                width={isMobile ? 30 : 40}
              />
              <Tooltip
                formatter={(value, name, props) => [value, props.payload.fullName]}
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid #E5E7EB",
                  borderRadius: "8px",
                  fontSize: isMobile ? "11px" : "14px",
                }}
              />
              <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={isMobile ? 20 : 40}>
                {statusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}